import React, { useState } from 'react';
import { SelectBox } from 'devextreme-react/select-box';
import { Validator, RequiredRule } from 'devextreme-react/validator';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronUp, faChevronDown } from '@fortawesome/free-solid-svg-icons';
import '../../scss/controls/_dropdownbox.scss';
import { ValidatedEvent } from 'devextreme/ui/validator';
import { ValueChangedEvent } from 'devextreme/ui/select_box';
import { InfoIconButton } from './InfoIconButton';

export enum DropDownBoxComponentSpacing {
    None = 'noSpacing',
    SmallSpacing = 'smallSpacing',
    StandardSpacing = 'standardSpacing',
    LargeSpacing = 'largeSpacing',
}

export enum DropDownBoxLabelSize {
    Small = 'small',
    Medium = 'medium',
}

export enum DropDownBoxSearchMode {
    Contains = 'contains',
    StartsWith = 'startswith',
}

/** Where the label sits relative to the dropdown. */
export enum DropDownBoxLayoutStyle {
    /** Label above the dropdown (default; the original layout). */
    Vertical = 'vertical',
    /** Label to the left of the dropdown, in a fixed-width column (see labelWidth). */
    Horizontal = 'horizontal',
}

interface DropBoxProps {
    id: string;
    value: any;
    readOnly?: boolean;
    onChange: (value: any) => void;
    label?: any;
    helperText?: string;
    required?: boolean;
    children?: React.ReactNode;
    validator?: React.ReactNode;
    dataSource?: any;
    displayExpr?: string;
    valueExpr?: string;
    placeholder?: string;
    width?: string;
    height?: string;
    disabled?: boolean;
    componentSpacing?: DropDownBoxComponentSpacing;
    onValidationEvent?: (isValid: boolean) => void;
    grouped?: boolean;
    showInfoIcon?: boolean;
    infoText?: React.ReactNode;
    labelSize?: DropDownBoxLabelSize;
    searchEnabled?: boolean;
    searchMode?: DropDownBoxSearchMode;
    /** Label above the dropdown (Vertical, default) or beside it (Horizontal). */
    layoutStyle?: DropDownBoxLayoutStyle;
    /** Width of the label column when layoutStyle is Horizontal. Defaults to 120px. Ignored for Vertical. */
    labelWidth?: string;
}

export const DropDownBox: React.FC<DropBoxProps> = ({
    id,
    value,
    readOnly = false,
    onChange,
    label,
    helperText,
    required = false,
    children,
    validator,
    dataSource,
    displayExpr,
    valueExpr,
    placeholder,
    width = 'auto',
    height = '54px',
    disabled = false,
    componentSpacing = DropDownBoxComponentSpacing.StandardSpacing,
    onValidationEvent,
    grouped = false,
    showInfoIcon = false,
    infoText,
    labelSize = DropDownBoxLabelSize.Small,
    searchEnabled = true,
    searchMode = DropDownBoxSearchMode.StartsWith,
    layoutStyle = DropDownBoxLayoutStyle.Vertical,
    labelWidth,
}) => {
    const [errorMessage, set_errorMessage] = useState('');
    const [dropdownVisible, setDropdownVisible] = useState(false);

    // Vertical keeps the exact class name and inline style the control has always rendered, so existing
    // usages are unaffected; Horizontal only adds a class (+ the label width as a CSS variable).
    const isHorizontal = layoutStyle === DropDownBoxLayoutStyle.Horizontal;
    const containerClassName = `dropdownbox-container ${componentSpacing}${isHorizontal ? ' horizontal' : ''}`;
    const containerStyle: React.CSSProperties = isHorizontal
        ? ({ width: width ? width : 'auto', '--dropdownbox-label-width': labelWidth ?? '120px' } as React.CSSProperties)
        : { width: width ? width : 'auto' };

    const handleValueChanged = (e: ValueChangedEvent) => {
        onChange((e?.value ?? null) as any);
    };

    // Open dropdown if closed; when open, prevent submit/validation
    const handleEnter = (e: any) => {
        const isOpen = e.component.option?.('opened');
        if (!isOpen && !readOnly && !disabled) {
            e.event.preventDefault();
            e.component.open();
            return;
        }

        // If already open, prevent Enter from submitting the form
        e.event.preventDefault();
        e.event.stopPropagation();
    };

    const onValidated = (validatedInfo: ValidatedEvent) => {
        set_errorMessage(validatedInfo?.brokenRule?.message ?? '');

        if (onValidationEvent) {
            const isValid = validatedInfo.isValid ?? false;
            onValidationEvent(isValid);
        }
    };

    const renderDropDownIcon = () => (
        <div style={{ alignContent: 'center' }}>
            <FontAwesomeIcon
                icon={dropdownVisible ? faChevronUp : faChevronDown}
                aria-hidden='true'
            />
        </div>
    );

    return (
        <div
            className={containerClassName}
            style={containerStyle}
        >
            {label && (
                <div className={`dropdownbox-label-wrapper ${labelSize}`}>
                    <label className={`dropdownbox-label ${labelSize} ${errorMessage ? 'error' : ''}`}>
                        {required ? (
                            <>
                                {label}
                                <span> *</span>
                            </>
                        ) : (
                            label
                        )}
                    </label>
                    {showInfoIcon && <InfoIconButton modalText={infoText} />}
                </div>
            )}
            <div
                className='dropdownbox-wrapper'
                style={{ position: 'relative' }}
            >
                <SelectBox
                    id={id}
                    value={value ?? null}
                    readOnly={readOnly}
                    onValueChanged={handleValueChanged}
                    dataSource={dataSource}
                    displayExpr={displayExpr}
                    valueExpr={valueExpr}
                    className={`dropdownbox-input ${errorMessage ? 'error' : ''}`}
                    style={{ height }}
                    onOpened={() => setDropdownVisible(true)}
                    onClosed={() => setDropdownVisible(false)}
                    onEnterKey={handleEnter}
                    dropDownButtonRender={renderDropDownIcon}
                    placeholder={placeholder}
                    width={width}
                    disabled={disabled}
                    searchEnabled={searchEnabled}
                    searchMode={searchMode}
                    grouped={grouped}
                    height={height}
                >
                    <Validator onValidated={onValidated}>
                        {required && <RequiredRule message='This field is required' />}
                        {validator}
                    </Validator>
                </SelectBox>
            </div>
            {/* Show helper text only if there is no error */}
            {errorMessage ? (
                <div className='dropdownbox-error'>{errorMessage}</div>
            ) : (
                helperText && <div className='dropdownbox-helper'>{helperText}</div>
            )}
            {children && <div className='dropdownbox-children'>{children}</div>}
        </div>
    );
};
